import { ESdChangeReason, IDocumentParameterInput } from "@kuika/kuika-cl-model";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import { ActionPicker } from "../properties-panel/action-picker";
import ActionSectionEmptyState from "../properties-panel/action-section-empty-state";
import { ActionSectionAddButton, ActionSectionTitle } from "../properties-panel/action-section-header";
import { DocumentParameterInputPanel, EDocumentParameterInputPanelStatus } from "./document-parameter-input-panel";
import "./document-parameters-panel.scss";

export interface IDocumentParametersPropertiesProps {}

export interface IDocumentParametersPropertiesState {
  /** A not-yet-created input. Held here so cancelling leaves the saved list untouched. */
  draftInput?: IDocumentParameterInput;
}

/**
 * Right panel for the Document Parameters tool (KOA-17254).
 *
 * <b>The split between the two surfaces.</b> The canvas answers one question — which tag gets which value — and is
 * a table. Everything a value can be *built from* is authored here: the declared document inputs, and the source
 * actions whose results fill tags. Putting the inputs in the canvas made it two unrelated forms stacked above a
 * table, and it separated the inputs from the action parameters that reference them, which are edited here.
 *
 * <b>Why the action picker and not a dropdown.</b> An earlier attempt listed every action definition in the project
 * in a select on each parameter row, then a cascader inside the canvas. Both were wrong for the same reason: adding
 * an action is an established interaction in this product, with search, grouping, parameter panels, reordering and
 * paste — reimplementing a lesser version of it in a settings form makes the feature feel foreign and drops
 * everything the real picker already does.
 *
 * The tool is its own host, so unlike scheduled jobs or api methods there is no sub-entity to select first: both
 * lists are available as soon as the surface is open.
 */
class DocumentParametersProperties extends PureSdComponent<
  IDocumentParametersPropertiesProps & IWithSDProps,
  IDocumentParametersPropertiesState
> {
  constructor(props: IDocumentParametersPropertiesProps & IWithSDProps) {
    super(props);
    this.state = { draftInput: undefined };
  }

  private inputs = (): IDocumentParameterInput[] => this.props.sd?.documentParameterInputs ?? [];

  private commit = (inputs: IDocumentParameterInput[], successMessage: string): void => {
    // Through the provider, which owns every part of the section: the inputs and the parameter rows are stored
    // together, so a save here must carry the definitions as they currently stand.
    //
    // Both outcomes are reported. Dropping the returned promise is what turned every failed write on this surface
    // into "it does not save" — the request went out, something refused it, and nothing said so.
    this.props.sd
      .saveDocumentParameterDefinitions(this.props.sd.documentParameterDefinitions ?? [], inputs)
      .then(() => KMainFunctions.displaySuccessNotification(successMessage))
      .catch((error: unknown) => KMainFunctions.exceptionHandler(error));
  };

  private startNewInput = (): void => {
    this.setState({ draftInput: { name: "", typeName: "String", cardinality: 1 } });
  };

  private saveNewInput = (input: IDocumentParameterInput): void => {
    this.setState({ draftInput: undefined }, () =>
      this.commit([...this.inputs(), input], `Document input "${input.name}" saved`)
    );
  };

  private saveExistingInput = (index: number, input: IDocumentParameterInput): void => {
    const next: IDocumentParameterInput[] = this.inputs().map((candidate: IDocumentParameterInput, position: number) =>
      position === index ? input : candidate
    );
    this.commit(next, `Document input "${input.name}" saved`);
  };

  private removeInput = (index: number): void => {
    const removed: string = this.inputs()[index]?.name ?? "";
    this.commit(
      this.inputs().filter((_input: IDocumentParameterInput, position: number) => position !== index),
      `Document input "${removed}" removed`
    );
  };

  private renderInputs() {
    const inputs: IDocumentParameterInput[] = this.inputs();
    const { draftInput } = this.state;

    return (
      <div className="kuika_Wrapper kuika-document-parameters-panel">
        {/* The same section chrome as Actions, Screen Inputs and Validations: title + count and the "+" at the
            right edge, then this section's own hint, then the dashed empty card while there is nothing to list. */}
        <div className="kuika_actionPanelWrapperHeader kuika-document-parameters-panel__section">
          <div className="kuika-action-section__header-row">
            <ActionSectionTitle title="Document Inputs" count={inputs.length} />
            <span className="kuika-action-section__add-slot">
              <ActionSectionAddButton
                ariaLabel="Add document input"
                testId="addDocumentInputButton"
                onClick={this.startNewInput}
              />
            </span>
          </div>
          <KuikaSmallText className="kuika-document-parameters-panel__hint">
            Supplied at runtime: bound once on the Resolve Document Parameters action, then available to every source
            action.
          </KuikaSmallText>
        </div>

        <div className="kuika-document-parameters-panel__inputs">
          {inputs.length === 0 && !draftInput && (
            <ActionSectionEmptyState
              flush
              message="No document inputs yet."
              actionLabel="Add document input"
              onAdd={this.startNewInput}
              testId="documentInputsEmptyState"
            />
          )}
          {inputs.map((input: IDocumentParameterInput, index: number) => (
            <DocumentParameterInputPanel
              key={`document-parameter-input-${input.name}-${index}`}
              input={input}
              status={EDocumentParameterInputPanelStatus.edit}
              existingNames={inputs
                .filter((_candidate: IDocumentParameterInput, position: number) => position !== index)
                .map((candidate: IDocumentParameterInput) => candidate.name)}
              onSaved={(saved: IDocumentParameterInput) => this.saveExistingInput(index, saved)}
              onRemoved={() => this.removeInput(index)}
            />
          ))}

          {draftInput && (
            <DocumentParameterInputPanel
              key="document-parameter-input-draft"
              input={draftInput}
              status={EDocumentParameterInputPanelStatus.new}
              existingNames={inputs.map((candidate: IDocumentParameterInput) => candidate.name)}
              onSaved={this.saveNewInput}
              onRemoved={() => this.setState({ draftInput: undefined })}
            />
          )}
        </div>
      </div>
    );
  }

  render() {
    return (
      <div className="kuika_properties-window__content">
        {this.renderInputs()}
        <ActionPicker customHeader="Source Actions" />
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.OtherSetStates
    );
  };
}

const documentParametersProperties = withSD<IDocumentParametersPropertiesProps>(DocumentParametersProperties);
export { documentParametersProperties as DocumentParametersProperties };
